import { Button, Group, HoverCard, Menu, Modal, Stack, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import {
  IconCashPlus,
  IconChevronDown,
  IconEye,
  IconInfoCircle,
  IconInvoice,
} from '@tabler/icons-react';
import classNames from 'classnames/bind';
import { Panel } from 'components/Panel';
import dayjs from 'dayjs';
import { i18nKeys, useTranslation } from 'locales';
import { useContext } from 'react';
import { useHistory } from 'react-router';
import { isNonNull, isNullish } from 'remeda';
import Amount from 'shared/components/Amount';
import EncodePaymentForm from 'shared/forms/EncodePaymentForm';
import { useCanUseManualActions } from 'shared/utils/connector';
import { isPaid } from 'shared/utils/invoice-status';
import { creditNoteActions } from 'store/creditNote/creditnote.actions';
import { invoiceActions } from 'store/invoice/invoice.actions';
import { DialogShowId, dialogHide } from 'store/view/view.actions';
import InvoiceDetailContext from '../InvoiceDetail.context';
import styleIdentifiers from '../ToHandleDetail.module.scss';
import LatePaymentInfoTermsModal from './LatePaymentInfoTermsModal';

const styles = classNames.bind(styleIdentifiers);

export function LatePaymentInfo() {
  const { t } = useTranslation();
  const { data, isCreditNote, unmatchPayment, clientData, reloadInfo, noValidate } =
    useContext(InvoiceDetailContext);

  const [
    isEncodePaymentModalOpen,
    { open: openEncodePaymentModal, close: closeEncodePaymentModal },
  ] = useDisclosure(false);

  const history = useHistory();
  const hasManualActionsActive = useCanUseManualActions();
  const canShowInvoiceButtons = !noValidate && !isCreditNote && hasManualActionsActive;

  const submitPayment =
    (invoiceId: number, reload: () => void, currency: string) =>
    (values: Record<string, unknown>) => {
      invoiceActions.actionsAddPayment({
        id: invoiceId,
        data: {
          currency,
          ...values,
        },
        callback: () => {
          reload();
          closeEncodePaymentModal();
        },
      });
    };

  const createCreditNote = (reference: string) => () => {
    dialogHide(DialogShowId.INVOICE);
    creditNoteActions.detailReset();
    history.push(`/credit-notes/new?reference=${reference}`);
  };

  const canUnmatch = (() => {
    if (isCreditNote) return Number(data.remaining_balance) !== Number(data.total_tvac);
    if (noValidate) return false;
    if (data.actions.includes('unmatch_payment')) return true;
    if ((data.credit_notes_attributes ?? []).length > 0) return true; // Sentry 181

    return false;
  })();

  const shouldShowActionMenu = canUnmatch || canShowInvoiceButtons;

  const showCgvDetails = () => {
    modals.open({
      title: t(i18nKeys.SALE_CONDITION_APPLIED),
      children: <LatePaymentInfoTermsModal invoice={data} />,
    });
  };

  const onClickUnmatch = (e: React.MouseEvent) => {
    e.stopPropagation();
    unmatchPayment?.();
  };

  return (
    <>
      <Panel
        pb={0}
        withBorder
        title={t(i18nKeys.INVOICE.PAYMENT_INFO)}
        className={styles('box')}
        topRight={
          shouldShowActionMenu && (
            <Menu position="bottom-end" withArrow>
              <Menu.Target>
                <Button.Group>
                  {canUnmatch && (
                    <Button onClick={onClickUnmatch} size="compact-sm" variant="default">
                      <IconEye size={20} stroke={1.5} />
                    </Button>
                  )}
                  <Button
                    rightSection={<IconChevronDown size={20} stroke={1.5} />}
                    size="compact-sm"
                    variant="default"
                  >
                    {t(i18nKeys.FORM.ACTIONS)}
                  </Button>
                </Button.Group>
              </Menu.Target>
              <Menu.Dropdown>
                {canShowInvoiceButtons && (
                  <>
                    <Menu.Item
                      disabled={!data.total_htva}
                      onClick={createCreditNote(data.reference)}
                      leftSection={<IconInvoice size={20} stroke={1.5} />}
                    >
                      {t(i18nKeys.INVOICE.CREATE_CREDIT_NOTE)}
                    </Menu.Item>
                    <Menu.Item
                      onClick={openEncodePaymentModal}
                      disabled={!data.actions?.includes('add_payment')}
                      leftSection={<IconCashPlus size={20} stroke={1.5} />}
                    >
                      {t(i18nKeys.INVOICE.ADD_PAYMENT)}
                    </Menu.Item>
                  </>
                )}
              </Menu.Dropdown>
            </Menu>
          )
        }
      >
        <div className={styles('item-container')}>
          <div className={styles('item')}>
            <span className={styles('subtitle')}>{t(i18nKeys.DUE_DATE)}</span>
            <span className={styles('value', 'due-date')}>
              <span className={styles('value', 'blue')}>
                {dayjs(data.due_date.toISOString()).format('L')}
              </span>
              <span className={styles('value', 'medium', 'red', 'ml-10')}>
                + {t(i18nKeys.DAYS, { count: data.days_late || 0 })}
              </span>
            </span>
          </div>
          {!isPaid(data) && clientData?.average_payment_delay !== null && (
            <div className={styles('item')}>
              <span className={styles('subtitle')}>
                {t(i18nKeys.INVOICE.ESTIMATED_PAYMENT_DATE)}
              </span>
              <div>
                {data.issue_date ? (
                  <span className={styles('value', 'blue')}>
                    {dayjs(data.estimated_payment_date).format('L')}
                  </span>
                ) : (
                  <span className={styles('value', 'blue')}>-</span>
                )}
              </div>
            </div>
          )}
          {!isCreditNote && isNullish(data.ignored_late_fees) && (
            <div className={styles('item')}>
              <span className={styles('subtitle')}>{t(i18nKeys.INVOICE.TOTAL_LATE_FEES)}</span>
              <div className={styles('value')}>
                <Group>
                  <Amount
                    localizedValue={data.late_fees}
                    value={data.localized_money_object.late_fees}
                    suffix={data.currency}
                    className={styles('bigger')}
                  />
                  <HoverCard withArrow position="bottom-end" shadow="sm">
                    <HoverCard.Target>
                      <IconInfoCircle size={18} stroke={1.5} />
                    </HoverCard.Target>
                    <HoverCard.Dropdown>
                      <Stack>
                        {data.interest_amount > 0 && (
                          <span>
                            <Text c="dimmed">{`${t(i18nKeys.INVOICE.INTEREST)} (${dayjs(data.late_fees_calculated_at).format('L')})`}</Text>
                            <Text fw={600}>
                              <Amount
                                value={data.localized_money_object.interest_amount}
                                suffix={data.currency}
                                className={styles('value')}
                                tooltip={false}
                                localizedValue={data.interest_amount}
                              />
                            </Text>
                          </span>
                        )}
                        {data.fix_fee_amount > 0 && (
                          <span>
                            <Text c="dimmed">{t(i18nKeys.FIX_FEE_AMOUNT)}</Text>
                            <Text fw={600}>
                              <Amount
                                value={data.localized_money_object.fix_fee_amount}
                                suffix={data.currency}
                                className={styles('value')}
                                tooltip={false}
                                localizedValue={data.fix_fee_amount}
                              />
                            </Text>
                          </span>
                        )}
                        {data.penalty_clause_amount > 0 && (
                          <span>
                            <Text c="dimmed">{t(i18nKeys.PENALTY_CLAUSE)}</Text>
                            <Text fw={600}>
                              <Amount
                                value={data.localized_money_object.penalty_clause_amount}
                                suffix={data.currency}
                                className={styles('value')}
                                tooltip={false}
                                localizedValue={data.penalty_clause_amount}
                              />
                            </Text>
                          </span>
                        )}
                        <Button size="compact-xs" variant="light" onClick={showCgvDetails}>
                          {t(i18nKeys.SHOW_CGV)}
                        </Button>
                      </Stack>
                    </HoverCard.Dropdown>
                  </HoverCard>
                </Group>
              </div>
            </div>
          )}
          {!isCreditNote && isNonNull(data.ignored_late_fees) && (
            <div className={styles('item')}>
              <span className={styles('subtitle')}>
                {t(i18nKeys.INVOICE.TOTAL_IGNORED_LATE_FEES)}
              </span>
              <div className={styles('value')}>
                <Amount
                  localizedValue={data.ignored_late_fees}
                  value={data.localized_money_object.ignored_late_fees}
                  suffix={data.currency}
                  className={styles('bigger')}
                />
              </div>
            </div>
          )}
          <div className={styles('item')}>
            {data.remaining_balance_with_fees === data.remaining_balance || isCreditNote ? (
              <>
                <span className={styles('subtitle')}>{t(i18nKeys.BALANCE)}</span>
                <span>
                  <Amount
                    localizedValue={data.remaining_balance}
                    value={data.localized_money_object.remaining_balance}
                    suffix={data.currency}
                    className={styles('value')}
                  />
                </span>
              </>
            ) : (
              <>
                <span className={styles('subtitle')}>{t(i18nKeys.INVOICE.BALANCE_FEES)}</span>
                <span className={styles('value', 'inline')}>
                  <Amount
                    localizedValue={data.remaining_balance}
                    value={data.localized_money_object.remaining_balance}
                    suffix={data.currency}
                    style={{ display: ' inline-block' }}
                  />
                  {' / '}
                  <Amount
                    localizedValue={data.remaining_balance_with_fees}
                    value={data.localized_money_object.remaining_balance_with_fees}
                    suffix={data.currency}
                    style={{ display: ' inline-block' }}
                  />
                </span>
              </>
            )}
          </div>
        </div>
      </Panel>
      <Modal
        size="auto"
        opened={isEncodePaymentModalOpen}
        onClose={closeEncodePaymentModal}
        title={t(i18nKeys.SHARED.ENCODE_PAYMENT.TITLE)}
      >
        <EncodePaymentForm
          onClose={closeEncodePaymentModal}
          onSubmit={submitPayment(data.id, reloadInfo, data.currency)}
          currency={data.currency}
          amountWithoutFees={data.remaining_balance_with_fees - data.late_fees}
          totalAmount={data.remaining_balance_with_fees}
        />
      </Modal>
    </>
  );
}
